/**
 * カタログ items を 1 件単位で手動入力・編集するダイアログ。
 * Excel/CSV 取込以外に商品を追加・編集するための入口。
 *
 * 動作:
 *  - existing 未指定 (null): 新規作成 (position は自動で末尾)
 *  - existing 指定: 編集
 *  - 税抜価格を入力すると税込価格は 10% 自動計算 (空欄時のみ補助。手入力で上書き可)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import {
  useUpsertCatalogItem,
  type CatalogItemRecord,
} from '@/hooks/useProductCatalogs';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalogId: string;
  /** null = 新規作成 / object = 既存編集 */
  existing: CatalogItemRecord | null;
}

interface FormState {
  name: string;
  modelName: string;
  variation: string;
  sku: string;
  productCategory: string;
  description: string;
  costPrice: string;
  priceExcludingTax: string;
  priceIncludingTax: string;
  supplierName: string;
  excludeFromManufacturerOrder: boolean;
}

const EMPTY: FormState = {
  name: '',
  modelName: '',
  variation: '',
  sku: '',
  productCategory: '',
  description: '',
  costPrice: '',
  priceExcludingTax: '',
  priceIncludingTax: '',
  supplierName: '',
  excludeFromManufacturerOrder: false,
};

function fromExisting(item: CatalogItemRecord): FormState {
  return {
    name: item.name,
    modelName: item.modelName,
    variation: item.variation ?? '',
    sku: item.sku,
    productCategory: item.productCategory ?? '',
    description: item.description ?? '',
    costPrice: String(item.costPrice),
    priceExcludingTax: String(item.priceExcludingTax),
    priceIncludingTax: String(item.priceIncludingTax),
    supplierName: item.supplierName ?? '',
    excludeFromManufacturerOrder: item.excludeFromManufacturerOrder ?? false,
  };
}

/**
 * 「製品カテゴリ」入力の候補 (自由入力も可)。
 * 2026-08-18 時点の TimeWaver 価格表に実在する値。カタログ提供元が増やす可能性があるため
 * ホワイトリスト検証はしない (サーバ側も検証しない)。
 */
const PRODUCT_CATEGORY_SUGGESTIONS = [
  'TimeWaver Pro',
  'Mobile Quantum',
  'Hardware',
  'Module',
  'Biz',
  'Update',
  'Coach',
  'Agrar',
  'Frequency',
  'Accessory',
  'RealTimeWaver',
];

function parseNum(s: string): number {
  const v = Number(s.replace(/[^\d.-]/g, ''));
  return Number.isFinite(v) ? v : 0;
}

export function CatalogItemEditorDialog({
  open,
  onOpenChange,
  catalogId,
  existing,
}: Props) {
  const [form, setForm] = useState<FormState>(EMPTY);
  // IME 中の親 state 更新でカーソル末尾飛びを防ぐ
  const composingRef = useRef(false);
  const upsert = useUpsertCatalogItem();

  useEffect(() => {
    if (composingRef.current) return;
    if (open) {
      setForm(existing ? fromExisting(existing) : EMPTY);
    }
  }, [open, existing]);

  const isEditing = !!existing;
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // 税抜入力時に税込が未入力なら 10% で自動補助
  const handleExcludingBlur = () => {
    const ex = parseNum(form.priceExcludingTax);
    if (ex > 0 && !form.priceIncludingTax.trim()) {
      set('priceIncludingTax', String(Math.round(ex * 1.1)));
    }
  };

  const requiredFilled = useMemo(
    () =>
      form.name.trim() &&
      form.modelName.trim() &&
      form.sku.trim() &&
      form.priceExcludingTax.trim() &&
      form.priceIncludingTax.trim(),
    [form],
  );

  const handleSubmit = async () => {
    if (!requiredFilled) {
      toast.error('商品名・型番・SKU・税抜価格・税込価格は必須です');
      return;
    }
    try {
      const res = await upsert.mutateAsync({
        catalogId,
        itemId: existing?.id,
        name: form.name.trim(),
        modelName: form.modelName.trim(),
        variation: form.variation.trim() || null,
        sku: form.sku.trim(),
        productCategory: form.productCategory.trim() || null,
        description: form.description.trim() || null,
        costPrice: parseNum(form.costPrice),
        priceExcludingTax: parseNum(form.priceExcludingTax),
        priceIncludingTax: parseNum(form.priceIncludingTax),
        supplierName: form.supplierName.trim() || null,
        excludeFromManufacturerOrder: form.excludeFromManufacturerOrder,
      });
      toast.success(
        res.created ? '商品を追加しました' : '商品を更新しました',
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`保存失敗: ${(err as Error).message}`);
    }
  };

  const composeProps = {
    onCompositionStart: () => (composingRef.current = true),
    onCompositionEnd: () => (composingRef.current = false),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? '商品を編集' : '商品を手動追加'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'カタログ内の商品 1 件を編集します。'
              : 'カタログに 1 件ずつ商品を追加します。Excel/CSV 取込と同じフィールド構成です。'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">SKU (カタログ内ユニーク)</Label>
              <Input
                value={form.sku}
                onChange={(e) => set('sku', e.target.value)}
                placeholder="BW-3.0-STD"
                className="font-mono"
                {...composeProps}
              />
            </div>
            <div>
              <Label className="text-xs">型番</Label>
              <Input
                value={form.modelName}
                onChange={(e) => set('modelName', e.target.value)}
                placeholder="Bio-Well 3.0"
                {...composeProps}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">商品名</Label>
              <Input
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Bio-Well 3.0 Camera スタンダード"
                {...composeProps}
              />
            </div>
            <div>
              <Label className="text-xs">バリエーション (任意)</Label>
              <Input
                value={form.variation}
                onChange={(e) => set('variation', e.target.value)}
                placeholder="Standard / Pro / Ultimate"
                {...composeProps}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">説明文 (任意)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="商品の補足説明..."
              rows={2}
              {...composeProps}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">仕入価格 (税込円)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.costPrice}
                onChange={(e) => set('costPrice', e.target.value)}
                placeholder="300000"
                className="font-mono text-right"
              />
            </div>
            <div>
              <Label className="text-xs">販売価格 (税抜円)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.priceExcludingTax}
                onChange={(e) => set('priceExcludingTax', e.target.value)}
                onBlur={handleExcludingBlur}
                placeholder="500000"
                className="font-mono text-right"
              />
            </div>
            <div>
              <Label className="text-xs">販売価格 (税込円)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.priceIncludingTax}
                onChange={(e) => set('priceIncludingTax', e.target.value)}
                placeholder="550000"
                className="font-mono text-right"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">製品カテゴリ (任意)</Label>
              <input
                list="catalog-product-categories"
                value={form.productCategory}
                onChange={(e) => set('productCategory', e.target.value)}
                placeholder="Module / Hardware / TimeWaver Pro ..."
                className="w-full rounded border border-[var(--border)] bg-[var(--bg-input)] text-sm px-3 py-2"
                {...composeProps}
              />
              <datalist id="catalog-product-categories">
                {PRODUCT_CATEGORY_SUGGESTIONS.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                価格表の「製品カテゴリ」列。商談の商品カテゴリーとは別物です。
              </p>
            </div>
            <div>
              <Label className="text-xs">仕入先名 (任意)</Label>
              <Input
                value={form.supplierName}
                onChange={(e) => set('supplierName', e.target.value)}
                placeholder="メーカー名・商社名等"
                {...composeProps}
              />
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2.5">
            <Switch
              checked={form.excludeFromManufacturerOrder}
              onChange={(e) =>
                set('excludeFromManufacturerOrder', e.target.checked)
              }
            />
            <div className="flex-1">
              <Label className="!mb-0">メーカー発注に含まない</Label>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                ON にすると、この商品は「メーカー見積依頼／正式発注」メールの商品欄
                （{'{{orderedProducts}}'} / {'{{productSummary}}'}）に挿入されません。
                自社内製品・付属品など、メーカーへ発注しない品目に使います。
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={upsert.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={!requiredFilled || upsert.isPending}
          >
            {upsert.isPending && <Spinner className="size-3 mr-1" />}
            {isEditing ? '更新' : '追加'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
